import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  MessageSquare,
  Users,
  Send,
  BarChart3,
  Activity,
  UserCheck,
  Target,
  ArrowDownLeft,
} from "lucide-react";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAnalytics, getUserEngagementAnalytics } from "../apis/instance";

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);

  // Fetch both analytics on mount
  const fetchAllAnalytics = async () => {
    setIsLoading(true);
    try {
      const [msgRes, engRes] = await Promise.all([
        getAnalytics(),
        getUserEngagementAnalytics(),
      ]);
      if (msgRes.data.success && msgRes.data.analytics) {
        setAnalyticsData(msgRes.data.analytics);
      } else {
        setAnalyticsData(null);
      }
      if (engRes.data.success && engRes.data.analytics) {
        setEngagementData(engRes.data.analytics);
      } else {
        setEngagementData(null);
      }
    } catch {
      setAnalyticsData(null);
      setEngagementData(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  // Prepare message volume data
  const messageVolumeData =
    analyticsData?.day_wise?.map((d) => ({
      name: d.date,
      messages: d.sent_count + d.failed_count,
    })) || [
      { name: "Mon", messages: 4000 },
      { name: "Tue", messages: 3000 },
      { name: "Wed", messages: 2000 },
      { name: "Thu", messages: 2780 },
      { name: "Fri", messages: 1890 },
      { name: "Sat", messages: 2390 },
      { name: "Sun", messages: 3490 },
    ];

  // Prepare engagement volume data
  const engagementVolumeData =
    engagementData?.day_wise?.map((d) => ({
      name: d.date,
      unique_users: d.unique_users,
      avg_score: Number(d.avg_score),
    })) || [
      { name: "Mon", unique_users: 400, avg_score: 65 },
      { name: "Tue", unique_users: 300, avg_score: 70 },
      { name: "Wed", unique_users: 200, avg_score: 68 },
      { name: "Thu", unique_users: 278, avg_score: 75 },
      { name: "Fri", unique_users: 189, avg_score: 72 },
      { name: "Sat", unique_users: 239, avg_score: 78 },
      { name: "Sun", unique_users: 80, avg_score: 82 },
    ];

  // Message statistics cards
  const messageStats = analyticsData
    ? [
        {
          label: "Total Messages",
          value: analyticsData.statistics.total_messages,
          icon: MessageSquare,
          color: "emerald",
        },
        {
          label: "Messages Sent",
          value: analyticsData.statistics.sent_count,
          icon: Send,
          color: "blue",
        },
        {
          label: "Messages Received",
          value: analyticsData.statistics.total_incoming_messages,
          icon: ArrowDownLeft,
          color: "cyan",
        },
        {
          label: "Failed Count",
          value: analyticsData.statistics.failed_count,
          icon: Target,
          color: "rose",
        },
        {
          label: "Success Rate",
          value: analyticsData.statistics.success_rate + "%",
          icon: UserCheck,
          color: "violet",
        },
      ]
    : [];

  // Engagement statistics cards
  const engagementStats = engagementData
    ? [
        {
          label: "Unique Engaging Users",
          value: engagementData.statistics.total_unique_users || 0,
          icon: Users,
          color: "emerald",
        },
        {
          label: "Avg Interactions/User",
          value: engagementData.statistics.total_avg_interactions
            ? engagementData.statistics.total_avg_interactions.toFixed(2)
            : engagementData.statistics.avg_interactions || 0,
          icon: Activity,
          color: "blue",
        },
        {
          label: "Avg Score/User",
          value: engagementData.statistics.total_avg_score
            ? engagementData.statistics.total_avg_score.toFixed(2)
            : engagementData.statistics.avg_score || 0,
          icon: BarChart3,
          color: "violet",
        },
      ]
    : [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, duration: 0.25 },
    },
  };

  const cardVariants = {
    hidden: { y: 18, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.45, ease: "easeOut" } },
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <motion.div
        className="mx-auto px-3 sm:px-4 lg:px-6 py-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Message Stats */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4"
          variants={containerVariants}
        >
          {messageStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ y: -4, scale: 1.01 }}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm ring-1 ring-gray-100"
              >
                <div className="flex items-start justify-between gap-2.5">
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-0.5">
                      {stat.label}
                    </p>
                    <motion.p
                      className="text-xl lg:text-2xl font-extrabold tracking-tight text-gray-900"
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.05 + 0.15, type: "spring", stiffness: 420 }}
                    >
                      {stat.value}
                    </motion.p>
                  </div>
                  <div
                    className={`
                      p-2 rounded-xl shadow-inner
                      ${
                        stat.color === "emerald"
                          ? "bg-[#01CC54]/10"
                          : stat.color === "blue"
                          ? "bg-primary/10"
                          : stat.color === "cyan"
                          ? "bg-primary/10"
                          : stat.color === "rose"
                          ? "bg-red-100"
                          : "bg-purple-100"
                      }
                      ring-1 ring-gray-200
                    `}
                  >
                    <Icon
                      size={18}
                      className={
                        stat.color === "emerald"
                          ? "text-[#01CC54]"
                          : stat.color === "blue"
                          ? "text-primary"
                          : stat.color === "cyan"
                          ? "text-primary"
                          : stat.color === "rose"
                          ? "text-red-600"
                          : "text-purple-600"
                      }
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Engagement Stats */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4"
          variants={containerVariants}
        >
          {engagementStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ y: -4, scale: 1.01 }}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm ring-1 ring-gray-100"
              >
                <div className="flex items-start justify-between gap-2.5">
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-0.5">
                      {stat.label}
                    </p>
                    <motion.p
                      className="text-xl lg:text-2xl font-extrabold tracking-tight text-gray-900"
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.05 + 0.15, type: "spring", stiffness: 420 }}
                    >
                      {stat.value}
                    </motion.p>
                  </div>
                  <div
                    className={`
                      p-2 rounded-xl shadow-inner bg-gradient-to-br ring-1 ring-white/10
                      ${
                        stat.color === "emerald"
                          ? "from-emerald-500/20 to-emerald-400/10"
                          : stat.color === "blue"
                          ? "from-blue-500/20 to-blue-400/10"
                          : "from-violet-500/20 to-violet-400/10"
                      }
                    `}
                  >
                    <Icon
                      size={18}
                      className={
                        stat.color === "emerald"
                          ? "text-emerald-300"
                          : stat.color === "blue"
                          ? "text-blue-300"
                          : "text-violet-300"
                      }
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Message Volume Area Chart */}
        <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">Message Volume</h3>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={messageVolumeData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="msgFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0.06} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(17,24,39,0.95)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8,
                    color: "#e5e7eb",
                  }}
                  itemStyle={{ color: "#e5e7eb" }}
                  labelStyle={{ color: "#a7f3d0" }}
                />
                <Area
                  type="monotone"
                  dataKey="messages"
                  stroke="#34d399"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#msgFill)"
                  name="Messages"
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Engagement Volume Area Chart */}
        <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">Engagement Volume</h3>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={engagementVolumeData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="engUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#60a5fa" stopOpacity={0.06} />
                  </linearGradient>
                  <linearGradient id="engScore" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#a78bfa" stopOpacity={0.06} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(17,24,39,0.95)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 8,
                    color: "#e5e7eb",
                  }}
                  itemStyle={{ color: "#e5e7eb" }}
                  labelStyle={{ color: "#93c5fd" }}
                />
                <Area
                  type="monotone"
                  dataKey="unique_users"
                  stroke="#60a5fa"
                  fill="url(#engUsers)"
                  strokeWidth={2}
                  name="Unique Users"
                  activeDot={{ r: 5 }}
                />
                <Area
                  type="monotone"
                  dataKey="avg_score"
                  stroke="#a78bfa"
                  fill="url(#engScore)"
                  strokeWidth={2}
                  name="Avg Score"
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;
